export interface NavGroup {
    key: string
    label: string
    icon: string
    order: number
    /** Nests this group inside another, by that group's key. */
    parent?: string
    /** `litta.applications.key` a role is needed on to see this section. */
    requiresApplication?: string
    /** A specific role (or any of several) on `requiresApplication`, rather than any at all. */
    requiresRole?: string | string[]
}

export interface NavRequirement {
    application: string
    /** Any one of these satisfies the requirement. Absent means any role at all. */
    roles?: string[]
}

export const NAV_GROUPS: NavGroup[] = [
    {
        key: 'dashboards',
        label: 'Dashboards',
        icon: 'material-symbols:dashboard',
        order: 2,
    },
    {
        key: 'sales-orders',
        label: 'Sales & Orders',
        icon: 'material-symbols:shopping-cart',
        order: 3,
    },
    {
        key: 'skips',
        label: 'Skips',
        icon: 'material-symbols:local-shipping',
        order: 3,
        parent: 'sales-orders',
    },
    {
        key: 'fulfilment-operations',
        label: 'Fulfilment Operations',
        icon: 'material-symbols:lan',
        order: 5,
    },
    {
        key: 'post-job',
        label: 'Post-Job Work',
        icon: 'material-symbols:fact-check',
        order: 6,
    },
    {
        key: 'customer-service',
        label: 'Customer Service',
        icon: 'material-symbols:support-agent',
        order: 7,
    },
    {
        key: 'commercial',
        label: 'Litta Business',
        icon: 'material-symbols:apartment',
        order: 8,
    },
    {
        key: 'supply-network',
        label: 'Supply Network',
        icon: 'material-symbols:group',
        order: 9,
        requiresApplication: 'beehive',
        requiresRole: ['admin', 'viewer'],
    },
    {
        key: 'developer',
        label: 'Developer',
        icon: 'material-symbols:code',
        order: 11,
        requiresApplication: 'beehive',
        requiresRole: 'engineer',
    },
    {
        key: 'lara',
        label: 'LARA',
        icon: 'material-symbols:smart-toy',
        order: 2,
        parent: 'developer',
    },
]

export interface NavLink {
    label: string
    href: string
    icon: string
    group: string
    order: number
}

/** Destinations outside Beehive, which have no route to hang sidebar meta on. */
export const NAV_LINKS: NavLink[] = [
    {
        label: 'Haystack',
        href: 'https://haystack.litta.co',
        icon: 'material-symbols:open-in-new',
        group: 'fulfilment-operations',
        order: 0,
    },
]

const byKey = new Map(NAV_GROUPS.map(group => [group.key, group]))

function toRoleList(role: string | string[] | undefined): string[] | undefined {
    if (role === undefined) return undefined
    return typeof role === 'string' ? [role] : role
}

/**
 * Every requirement that applies to a group, its own plus each ancestor's, so
 * a nested group cannot be reached by a route that skipped the parent's gate.
 */
export function requirementsForGroup(
    key: string | undefined,
): NavRequirement[] {
    const requirements: NavRequirement[] = []
    let current = key ? byKey.get(key) : undefined

    while (current) {
        if (current.requiresApplication) {
            requirements.push({
                application: current.requiresApplication,
                roles: toRoleList(current.requiresRole),
            })
        }
        current = current.parent ? byKey.get(current.parent) : undefined
    }
    return requirements
}

/**
 * What a route requires: its section's requirements, inherited, plus any it
 * declares itself. Gating the Developer group covers everything inside it
 * without every route repeating the flag.
 */
export function requirementsForRoute(meta: {
    requiresApplication?: string
    requiresRole?: string | string[]
    sidebarGroup?: string
}): NavRequirement[] {
    const own: NavRequirement[] = meta.requiresApplication
        ? [
              {
                  application: meta.requiresApplication,
                  roles: toRoleList(meta.requiresRole),
              },
          ]
        : []
    return [...requirementsForGroup(meta.sidebarGroup), ...own]
}

/** True when every requirement is met by the roles held. */
export function satisfies(
    requirements: NavRequirement[],
    roles: Record<string, string[]>,
): boolean {
    return requirements.every(({ application, roles: wanted }) => {
        const held = roles[application] ?? []
        if (!wanted) return held.length > 0
        return wanted.some(role => held.includes(role))
    })
}
